import {
  type AcceptInvitationRequest,
  type AuthSessionResponse,
  authSessionResponseSchema,
  CSRF_HEADER_NAME,
  CSRF_HEADER_VALUE,
  type LoginRequest,
  type PasswordResetConfirm,
  type PasswordResetRequest,
} from '@jaf/contracts';
import axios, { type AxiosInstance } from 'axios';
import { ApiError, toApiError } from './api-error';

const AUTH_TIMEOUT_MS = 10_000;

/**
 * Operações de sessão e senha que não usam bearer, em cliente HTTP próprio, sem interceptors
 * (evita refresh recursivo). Todas mandam cookies e o header anti-CSRF.
 */
export interface AuthClient {
  login: (credentials: LoginRequest) => Promise<AuthSessionResponse>;
  refresh: () => Promise<AuthSessionResponse>;
  logout: () => Promise<void>;
  requestPasswordReset: (request: PasswordResetRequest) => Promise<void>;
  confirmPasswordReset: (request: PasswordResetConfirm) => Promise<void>;
  /** USR-2: o convidado define a senha com o token do convite. */
  acceptInvitation: (request: AcceptInvitationRequest) => Promise<void>;
}

/** Cria o cliente de auth com cookies (withCredentials) e header anti-CSRF. */
export function createAuthClient(baseURL: string, instance?: AxiosInstance): AuthClient {
  const http =
    instance ??
    axios.create({
      baseURL,
      withCredentials: true,
      timeout: AUTH_TIMEOUT_MS,
      headers: { [CSRF_HEADER_NAME]: CSRF_HEADER_VALUE },
    });

  async function send(url: string, body?: object): Promise<unknown> {
    try {
      const { data } = await http.post<unknown>(url, body);
      return data;
    } catch (error) {
      throw toApiError(error);
    }
  }

  async function sessionFrom(url: string, body?: object): Promise<AuthSessionResponse> {
    const parsed = authSessionResponseSchema.safeParse(await send(url, body));
    if (!parsed.success) {
      throw new ApiError({ kind: 'contract', status: 0, title: 'Resposta de sessão inválida' });
    }
    return parsed.data;
  }

  return {
    login: (credentials) => sessionFrom('/auth/login', credentials),
    refresh: () => sessionFrom('/auth/refresh'),
    logout: async () => {
      await send('/auth/logout');
    },
    requestPasswordReset: async (request) => {
      await send('/auth/password-reset', request);
    },
    confirmPasswordReset: async (request) => {
      await send('/auth/password-reset/confirm', request);
    },
    acceptInvitation: async (request) => {
      await send('/auth/invitations/accept', request);
    },
  };
}
